import React, { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import settingsService from "../../services/settingsService";
import Icons from "../../components/icons";


export default function Contacts() {
  const [editContact, setEditContact] = useState(null);
  const [allContacts, setAllContacts] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all contacts from the correct endpoint
  const loadAllContacts = async () => {
    setIsLoadingAll(true);
    try {
      const data = await settingsService.getContacts();
      setAllContacts(data.success ? data.data : []);
    } catch (err) {
      setAllContacts([]);
    }
    setIsLoadingAll(false);
  };

  // Initial load
  useEffect(() => {
    loadAllContacts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredContacts = allContacts.filter((contact) => {
    const query = searchTerm.toLowerCase();
    if (!query) return true;

    return [
      contact.name,
      contact.email,
      contact.phone,
      contact.alternatePhone,
      designationLabel(contact.designation),
      roleLabel(contact.role),
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(query));
  });

  return (
    <div className="bg-white rounded-xl p-5 overflow-hidden mt-5">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="relative w-full sm:w-[313px] h-[37px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-full border rounded-lg pl-10 pr-3 text-sm outline-none"
          />
        </div>
      </div>

      {/* Table — styled like the Resources table */}
      <div className="mt-4 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {isLoadingAll ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#4253C7]" size={28} />
          </div>
        ) : filteredContacts.length === 0 ? (
          <p className="text-center text-gray-400 py-16">No contacts found.</p>
        ) : (
          <table className="w-full min-w-[640px] lg:min-w-0">
            <thead>
              <tr className="text-gray-400 text-[13px] border-b">
                <th className="pb-3 font-normal text-left w-[24%]">Contact</th>
                <th className="pb-3 font-normal text-center w-[16%]">Designation</th>
                <th className="pb-3 font-normal text-center w-[14%]">Phone</th>
                <th className="pb-3 font-normal text-center w-[14%]">Alt. Phone</th>
                <th className="pb-3 font-normal text-center w-[12%]">Role</th>
                <th className="pb-3 font-normal text-right w-[10%]">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact._id} className="border-b text-[15px] h-[49px]">
                  {/* Contact — avatar + name + email */}
                  <td className="py-[14px]">
                    <div className="flex items-center gap-2">
                      {contact.profilePicture ? (
                        <img
                          src={contact.profilePicture}
                          alt={contact.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[13px] font-semibold text-gray-600">
                          {contact.name?.[0] ?? "?"}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-normal text-[13px] text-black capitalize truncate">
                          {contact.name || "—"}
                        </p>
                        <p className="text-[11px] text-gray-400 lowercase truncate">
                          {contact.email}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Designation */}
                  <td className="text-[13px] text-center capitalize">
                    {designationLabel(contact.designation)}
                  </td>

                  {/* Phone */}
                  <td className="text-[13px] text-center">{contact.phone || "—"}</td>

                  {/* Alternate phone */}
                  <td className="text-[13px] text-center">{contact.alternatePhone || "—"}</td>

                  {/* Role */}
                  <td className="text-center">
                    <span className="text-[13px] px-3 py-1 rounded-full font-normal bg-[#CDDDFA] text-[#4379FE] capitalize">
                      {roleLabel(contact.role)}
                    </span>
                  </td>

                  {/* Action */}
                  <td className="text-right">
                    <button
                      onClick={() => setEditContact(contact)}
                      className="text-gray-300 hover:text-gray-500"
                      aria-label={`Edit ${contact.name || "contact"}`}
                    >
                      <Icons.Pencil />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Contact Modal */}
      {editContact && (
        <EditContactModal
          contact={editContact}
          onClose={() => setEditContact(null)}
          onSaved={(updatedFields) => {
            setAllContacts((prev) =>
              prev.map((c) =>
                c._id === editContact._id ? { ...c, ...updatedFields } : c
              )
            );
            setEditContact(null);
          }}
        />
      )}
    </div>
  );
}

// ── Designation helper ───────────────────────────────────────────────────────

const DESIGNATIONS = [
  { label: "Trainee Software Engineer - TSE",    value: "TSE"  },
  { label: "Associate Software Engineer - ASE",  value: "ASE"  },
  { label: "Software Engineer - SDE1",           value: "SDE1" },
  { label: "Senior Software Engineer - SDE2",    value: "SDE2" },
  { label: "Lead Software Engineer - SDE3",      value: "SDE3" },
  { label: "Technical Lead - TL",                value: "TL"   },
  { label: "Team Lead - TML",                    value: "TML"  },
  { label: "Technical Architect - TA",           value: "TA"   },
  { label: "Project Lead - PL",                  value: "PL"   },
  { label: "Associate Project Manager - APM",    value: "APM"  },
  { label: "Project Manager - PM",               value: "PM"   },
  { label: "Business Analyst - BA",              value: "BA"   },
  { label: "Senior Business Analyst - SBA",      value: "SBA"  },
  { label: "Test Engineer - TE",                 value: "TE"   },
  { label: "Senior Test Engineer - STE",         value: "STE"  },
  { label: "Testing Team Lead - TTL",            value: "TTL"  },
  { label: "UX Design Engineer - UXDE",          value: "UXDE" },
  { label: "Senior Design Engineer - SDEG",      value: "SDEG" },
  { label: "Business Development Manager - BDM", value: "BDM"  },
  { label: "Marketing & Sales Executive - MSE",  value: "MSE"  },
  { label: "Human Resources Executive - HR",     value: "HR"   },
  { label: "Senior HR Executive - SHR",          value: "SHR"  },
  { label: "HR Manager - HRM",                   value: "HRM"  },
  { label: "Assistant HR Manager - AHRM",        value: "AHRM" },
];

function designationLabel(designation) {
  const found = DESIGNATIONS.find((d) => d.value === designation);
  const label = found ? found.label : designation || "Software Engineer";
  return label.split(" - ")[0];
}

// ── Role badge helper ────────────────────────────────────────────────────────

function roleLabel(role) {
  switch (role) {
    case "admin":
      return "Admin";
    case "manager":
      return "Manager";
    case "viewer":
      return "Viewer";
    default:
      return "Member";
  }
}

// ── Edit Contact Modal ─────────────────────────────────────────────────────────

function EditContactModal({ contact, onClose, onSaved }) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState({
    name: contact.name || "",
    email: contact.email || "",
    phone: String(contact.phone || "").replace(/\D/g, ""),
    alternatePhone: String(contact.alternatePhone || "").replace(/\D/g, ""),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
   const nextValue =
  name === "phone" || name === "alternatePhone"
    ? value.replace(/\D/g, "").slice(0, 10)
    : value;
    setForm((p) => ({ ...p, [name]: nextValue }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const data = await settingsService.updateContact(contact._id, {
        name: form.name,
        phone: form.phone,
        alternatePhone: form.alternatePhone,
      });

      if (data.success) {
        onSaved(data.data);
      } else {
        setSaveError(data.message || "Failed to update contact.");
      }
    } catch (err) {
      setSaveError(
        err?.response?.data?.message || "Failed to update contact. Please try again."
      );
    }
    setIsSaving(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[635px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto p-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-[28px] font-semibold">Edit Contact</h2>
          <button onClick={onClose} className="text-black opacity-50 hover:opacity-100">
            <X size={28} />
          </button>
        </div>

        {/* Avatar + name + designation */}
        <div className="flex items-center gap-4 mt-8 mb-6">
          {contact.profilePicture ? (
            <img
              src={contact.profilePicture}
              alt={contact.name}
              className="w-[68px] h-[68px] rounded-full object-cover border border-gray-100"
            />
          ) : (
            <div className="w-[68px] h-[68px] rounded-full bg-gray-200 flex items-center justify-center text-xl font-bold text-gray-600 flex-shrink-0">
              {contact.name?.[0] ?? "?"}
            </div>
          )}
          <div>
            <p className="text-base font-normal text-black capitalize">{contact.name}</p>
            <p className="text-sm text-[#7F7F7F] capitalize">{designationLabel(contact.designation)}</p>
          </div>
        </div>

        {/* Name */}
        <div className="mb-5">
          <label className="block text-base text-[#7F7F7F] mb-2">Name</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full h-10 border border-black/20 rounded-lg px-3 text-base text-black outline-none focus:border-[#4253C7]"
          />
        </div>

        {/* Email */}
        <div className="mb-5">
          <label className="block text-base text-[#7F7F7F] mb-2">Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full h-10 border border-black/20 rounded-lg px-3 text-base text-black outline-none focus:border-[#4253C7]"
          />
        </div>

        {/* Phone + Alternative Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-base text-[#7F7F7F] mb-2">Phone</label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]*"
              className="w-full h-10 border border-black/20 rounded-lg px-3 text-base text-black outline-none focus:border-[#4253C7]"
            />
          </div>
          <div>
            <label className="block text-base text-[#7F7F7F] mb-2">Alternative Phone</label>
            <input
              type="tel"
              name="alternatePhone"
              value={form.alternatePhone}
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]*"
              className="w-full h-10 border border-black/20 rounded-lg px-3 text-base text-black outline-none focus:border-[#4253C7]"
            />
          </div>
        </div>

        {saveError && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-lg mb-4">
            {saveError}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="border border-[#4253C7] text-[#4253C7] w-[163px] h-[40px] rounded-lg text-sm flex items-center justify-center capitalize"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-[#4253C7] text-white w-[163px] h-[40px] rounded-lg text-sm flex items-center justify-center gap-2 capitalize disabled:opacity-60"
          >
            {isSaving && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </>
  );
}
